import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PaginatedResponse } from '@/types/api.types';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  totalRecoveredItems?: number;
}

// API Client response type
interface ApiClient {
  clientId: number;
  /**
   * Human-facing client number, unique and gap-free within the tenant.
   * Optional so a UI deployed ahead of the API degrades to "—" rather than
   * rendering "C-NaN".
   */
  clientNumber?: number;
  name: string | null;
  description: string | null;
  isActive: boolean;
  entity: string;
  parentClientId?: number | null;
  parentClientName?: string | null;
  deliveredPallets?: number;
  recoveredPallets?: number;
  transferredPallets?: number;
  lostPallets?: number;
  balance?: number;
}

/**
 * Renders a client number the way operators see it: `C-0001`.
 *
 * Zero-padded to 4 so it sorts correctly as text and reads as a business code
 * rather than a row id. Numbers past 9999 simply get longer.
 *
 * Returns an em dash for a client that has no number yet — only possible when
 * this UI is talking to an API deployed before the column existed.
 */
export const formatClientNumber = (n?: number | null): string =>
  n && n > 0 ? `C-${String(n).padStart(4, '0')}` : '—';

export interface Client {
  id: number;
  /**
   * Raw sequential number within the tenant. Use {@link formatClientNumber} to
   * display it; `code` already holds the formatted form.
   */
  clientNumber: number;
  /**
   * Display code shown to operators, e.g. `C-0001`.
   *
   * This used to be `String(clientId)` — the database primary key, on screen.
   * Besides leaking storage detail it looked arbitrary, because clientid is one
   * identity sequence shared across every tenant, so a tenant's own clients
   * appeared as 1, 5, 9, 14.
   */
  code: string;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  address?: string;
  parentClientId?: number | null;
  parentClientName?: string | null;
  deliveredPallets: number;
  recoveredPallets: number;
  transferredPallets: number;
  /** Confirmed losses deducted from `balance` (pallet-incidents module). */
  lostPallets: number;
  balance: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientSearchParams {
  search?: string;
  isActive?: boolean;
  status?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface CreateClientRequest {
  code: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  parentClientId?: number | null;
  adminUsername?: string;
  adminPassword?: string;
}

export interface UpdateClientRequest {
  id: number;
  code?: string;
  name?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  parentClientId?: number | null;
  isActive?: boolean;
  adminPassword?: string;
}

export interface ClientAdmin {
  securityUserId: number;
  userName: string;
  isActive: boolean;
}

export interface ClientStats {
  totalClients: number;
  activeClients: number;
  totalDelivered: number;
  totalRecovered: number;
  totalOutstanding: number;
}

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (params: ClientSearchParams) => [...clientKeys.lists(), params] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: number) => [...clientKeys.details(), id] as const,
  admin: (clientId: number) => [...clientKeys.all, 'admin', clientId] as const,
  dropdown: () => [...clientKeys.all, 'dropdown'] as const,
  stats: () => [...clientKeys.all, 'stats'] as const,
};

// Transform API client to our Client interface
function transformClient(apiClient: ApiClient): Client {
  return {
    id: apiClient.clientId,
    clientNumber: apiClient.clientNumber ?? 0,
    code: formatClientNumber(apiClient.clientNumber),
    name: apiClient.name || '',
    contactName: '',
    email: '',
    phone: '',
    address: apiClient.description || '',
    parentClientId: apiClient.parentClientId ?? null,
    parentClientName: apiClient.parentClientName ?? null,
    deliveredPallets: apiClient.deliveredPallets ?? 0,
    recoveredPallets: apiClient.recoveredPallets ?? 0,
    transferredPallets: apiClient.transferredPallets ?? 0,
    lostPallets: apiClient.lostPallets ?? 0,
    balance: apiClient.balance ?? 0,
    isActive: apiClient.isActive,
    createdAt: '',
    updatedAt: '',
  };
}

// Search clients
export function useClients(params: ClientSearchParams) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiClient[]>>('/api/client/list');
      let clients = response.data.data.map(transformClient);

      // Client-side filtering. /api/client/list has no Take/Skip — it returns
      // every client for the tenant — so both the filtering and the paging
      // below are ours, not the server's.
      if (params.search) {
        const search = params.search.toLowerCase();
        clients = clients.filter(c =>
          c.name.toLowerCase().includes(search) ||
          c.code.toLowerCase().includes(search)
        );
      }
      if (params.isActive !== undefined) {
        clients = clients.filter(c => c.isActive === params.isActive);
      }
      // `status` was declared on ClientSearchParams and passed by the Clients
      // Hub, but nothing ever read it — so that page's status dropdown silently
      // did nothing. It is the string form of the isActive filter above.
      if (params.status === 'active' || params.status === 'inactive') {
        const wantActive = params.status === 'active';
        clients = clients.filter(c => c.isActive === wantActive);
      }

      // Apply pagination
      const page = params.page || 1;
      const pageSize = params.pageSize || 25;
      const startIndex = (page - 1) * pageSize;
      const paginatedItems = clients.slice(startIndex, startIndex + pageSize);

      return {
        items: paginatedItems,
        /**
         * Every client matching the filters, unpaged.
         *
         * For callers that show the whole set rather than a page — the Clients
         * Hub renders cards with no pager, and its KPI tiles sum across all
         * clients. Reading `items` there silently truncated both to the first
         * 25. Nothing is fetched twice: the endpoint already returned this.
         *
         * Callers with a pager (catalogs/clients) should keep using `items`.
         */
        allItems: clients,
        totalCount: clients.length,
        totalPages: Math.ceil(clients.length / pageSize),
        page,
        pageSize,
        totalRecoveredItems: response.data.totalRecoveredItems ?? 0,
      } as PaginatedResponse<Client> & { allItems: Client[]; totalRecoveredItems: number };
    },
  });
}

// Get clients for dropdown
export function useClientsDropdown() {
  return useQuery({
    queryKey: clientKeys.dropdown(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiClient[]>>('/api/client/list');
      return response.data.data.map(transformClient);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Get client stats
export function useClientStats() {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiClient[]>>('/api/client/list');
      const clients = response.data.data;
      const totalDelivered = clients.reduce((sum, c) => sum + (c.deliveredPallets ?? 0), 0);
      const totalRecovered = clients.reduce((sum, c) => sum + (c.recoveredPallets ?? 0), 0);
      return {
        totalClients: clients.length,
        activeClients: clients.filter(c => c.isActive).length,
        totalDelivered,
        totalRecovered,
        totalOutstanding: totalDelivered - totalRecovered,
      } as ClientStats;
    },
  });
}

// Get single client
export function useClient(id: number, enabled = true) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiClient>>('/api/client/get', {
        params: { clientId: id },
      });
      return transformClient(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Get admin user for a client
export function useClientAdmin(clientId: number, enabled = true) {
  return useQuery({
    queryKey: clientKeys.admin(clientId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ClientAdmin | null>>(`/api/client/${clientId}/admin`);
      return response.data.data;
    },
    enabled: enabled && clientId > 0,
  });
}

// Create client
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClientRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/client/save-with-admin', {
        clientId: 0,
        name: data.name,
        description: data.address || data.contactName,
        isActive: true,
        parentClientId: data.parentClientId ?? null,
        userName: data.adminUsername || undefined,
        password: data.adminPassword || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
}

// Update client
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateClientRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/client/save-with-admin', {
        clientId: data.id,
        name: data.name,
        description: data.address || data.contactName,
        isActive: data.isActive ?? true,
        parentClientId: data.parentClientId ?? null,
        password: data.adminPassword || undefined,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
}

// Delete client
export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.post<ApiResponse<unknown>>('/api/client/delete', { clientId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
}

// Toggle client active status
export function useToggleClientActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      // Get current client data first
      const clientResponse = await api.get<ApiResponse<ApiClient>>('/api/client/get', {
        params: { clientId: id },
      });
      const client = clientResponse.data.data;

      // Update with new active status (preserve the F2 parent link —
      // the save endpoint overwrites every field it receives).
      const response = await api.post<ApiResponse<number>>('/api/client/save-with-admin', {
        clientId: id,
        name: client.name,
        description: client.description,
        isActive: isActive,
        parentClientId: client.parentClientId ?? null,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
}
